import axios from 'axios';
import { BASE_URL } from '../apiConfig';

/**
 * EUDR API Service
 * Handles communication with the EUDR backend endpoints
 */

// Custom backend URL for EUDR endpoints
const EUDR_BASE_URL = 'https://eudr-api.vertocommodities.io';

// Create axios instance for EUDR API
// Note: SSL certificate validation is handled by the browser
// For self-signed certificates in development, users need to accept the certificate in their browser
const eudrAxios = axios.create();

/**
 * Generate EUDR report with satellite imagery
 * @param {Object} params - Report generation parameters
 * @param {number} params.latitude - GPS latitude (if single point)
 * @param {number} params.longitude - GPS longitude (if single point)
 * @param {Array} params.polygon - Array of {lat, lon} objects (if polygon)
 * @param {string} params.fieldName - Name of the field
 * @param {string} params.accountId - User account ID
 * @param {number} params.zoom - Zoom level for imagery (default: 17)
 * @param {number} params.tileRadius - Tile radius (default: 2)
 * @returns {Promise} - API response with report data
 */
export const generateEudrReport = async (params) => {
  try {
    const response = await eudrAxios.post(`${EUDR_BASE_URL}/api/eudr/generateReport`, params);
    return response.data;
  } catch (error) {
    console.error('Error generating EUDR report:', error);
    throw error;
  }
};

/**
 * Quick check for available dates at a location
 * @param {number} latitude - GPS latitude
 * @param {number} longitude - GPS longitude
 * @param {number} zoom - Zoom level (default: 18)
 * @returns {Promise} - Available dates for the location
 */
export const quickCheckDates = async (latitude, longitude, zoom = 18) => {
  try {
    const response = await eudrAxios.post(`${EUDR_BASE_URL}/api/eudr/quickCheck`, {
      latitude,
      longitude,
      zoom
    });
    return response.data;
  } catch (error) {
    console.error('Error checking dates:', error);
    throw error;
  }
};

/**
 * Get all EUDR reports for an account
 * @param {string} accountId - User account ID
 * @returns {Promise} - List of reports
 */
export const getEudrReports = async (accountId) => {
  try {
    const response = await eudrAxios.get(`${EUDR_BASE_URL}/api/eudr/reports/${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching EUDR reports:', error);
    throw error;
  }
};

/**
 * Get specific report details
 * @param {string} reportId - Report ID
 * @returns {Promise} - Report details
 */
export const getEudrReport = async (reportId) => {
  try {
    const response = await eudrAxios.get(`${EUDR_BASE_URL}/api/eudr/report/${reportId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching EUDR report:', error);
    throw error;
  }
};

/**
 * Delete a report
 * @param {string} reportId - Report ID
 * @param {string} accountId - User account ID (for authorization)
 * @returns {Promise} - Deletion confirmation
 */
export const deleteEudrReport = async (reportId, accountId) => {
  try {
    const response = await eudrAxios.delete(`${EUDR_BASE_URL}/api/eudr/report/${reportId}`, {
      data: { accountId }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting EUDR report:', error);
    throw error;
  }
};

/**
 * Check if a report exists for a specific field (fast CosmosDB lookup)
 * @param {string} accountId - User account ID
 * @param {string} fieldName - Field name to check
 * @returns {Promise<{ exists: boolean, documentUrl?: string, generatedAt?: string }>}
 */
export const checkFieldHasReport = async (accountId, fieldName) => {
  try {
    const encodedField = encodeURIComponent(fieldName);
    // Calls VertoBackend (not the separate EUDR generation service)
    const response = await axios.get(`${BASE_URL}/api/eudr/checkField/${accountId}/${encodedField}`);
    return response.data;
  } catch (error) {
    console.error('Error checking field report:', error);
    return { exists: false };
  }
};
